import React, {useEffect, useMemo, useState} from 'react';
import {useParams} from "react-router-dom"
import {Layout} from '../components/common/Layout';
import {ApprovalStage} from '../components/application/admin/ApprovalStage';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {NoActionStage} from '../components/application/admin/NoActionStage';
import {UploadStage} from '../components/application/admin/UploadStage';
import {useMutation, useQuery} from "@apollo/client";
import {GET_APPLICATION_DETAILS, Stage} from "../api/application/getApplicationDetails";
import Typography from "@mui/material/Typography";
import {statusColorMap, statusMap} from "./Applications";
import {UPDATE_APPLICATION_STATUS} from "../api/application/updateApplicationStatus";

export const Application = () => {
    const {id} = useParams();
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [stages, setStages] = useState<Stage[]>([])

    const options = useMemo(() => {
        return ({
            variables: {
                applicationId: Number(id) ?? ''
            }
        })
    }, [id])

    const {loading, data, refetch} = useQuery(GET_APPLICATION_DETAILS, options);
    const [changeStatus, {loading: changeStatusLoading}] = useMutation(UPDATE_APPLICATION_STATUS)

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const onCloseApplication = async () => {
        if (!data) return

        const {errors} = await changeStatus({variables: {applicationId: data.showApplicationDetails.id, newStatus: 'REJECTED'}})

        if (errors === undefined || errors.length === 0) {
            await refetch()
            setIsModalOpen(false)
        }
    }

    useEffect(() => {
        if (data !== undefined) {
            setStages([...data.showApplicationDetails.applicationStages].sort((a, b) => a.order - b.order))
        }
    }, [data])

    return (
        <Layout
            chip={data ? data.showApplicationDetails.package.code : ''}
            isLoading={loading || changeStatusLoading}
            showHeader
            showMenu
            title={data ? data.showApplicationDetails.profile.company : ''}
        >
            <Stack alignItems="flex-end" marginBottom="2rem">
                <Button
                    color="error"
                    variant="outlined"
                    onClick={() => setIsModalOpen(true)}
                >
                    Close This Application
                </Button>

                <Dialog
                    fullScreen={fullScreen}
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                >
                    <DialogTitle>
                        Close Application
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Continuing will <strong>stop this application from being able to proceed!</strong> be 100% sure before proceeding. Close this application?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="error"
                            onClick={() => setIsModalOpen(false)}
                            variant="contained"
                        >
                            No, Go Back
                        </Button>
                        <Button
                            color="success"
                            onClick={onCloseApplication}
                            variant="contained"
                        >
                            Yes, Close it
                        </Button>
                    </DialogActions>
                </Dialog>
            </Stack>

            {
                stages.map((stage, index) => {
                    if (stage.stageStatus === 'PENDING') return null

                    const nextStageTitle = index < stages.length - 1
                        ? stages[index + 1].title
                        : undefined

                    switch (stage.stageType) {
                        case "APPROVAL": return (
                            <ApprovalStage
                                key={stage.id}
                                nextStageTitle={nextStageTitle}
                                refetch={() => refetch({applicationId: Number(id) ?? ''})}
                                stage={stage}
                            />
                        )


                        case "NO_ACTION": return (
                            <NoActionStage
                                key={stage.id}
                                nextStageTitle={nextStageTitle}
                                refetch={() => refetch({applicationId: Number(id) ?? ''})}
                                stage={stage}
                            />
                        )

                        case "UPLOAD": return (
                            <UploadStage
                                key={stage.id}
                                nextStageTitle={nextStageTitle}
                                refetch={() => refetch({applicationId: Number(id) ?? ''})}
                                stage={stage}
                            />
                        )
                    }
                })
            }

            {data && (
                <Stack marginTop="1rem">
                    <Stack direction="row">
                        <Typography width='10rem'>
                            Application status:
                        </Typography>
                        <Typography component='span' color={statusColorMap[data.showApplicationDetails.status]}>
                            {statusMap[data.showApplicationDetails.status]}
                        </Typography>
                    </Stack>

                    <Stack direction="row">
                        <Typography width='10rem'>
                            Last updated at:
                        </Typography>
                        <Typography component='span'>
                            {new Date(data.showApplicationDetails.updatedAt).toUTCString()}
                        </Typography>
                    </Stack>
                </Stack>
            )}
        </Layout>
    )
}
